import { RotateCcw, TriangleAlert, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteButton } from '@/components/DeleteButton';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { BulkItemLabel } from '@/lib/utils/bulk-delete';

export type BulkActionBarProps = {
	count: number;
	total: number;
	label: BulkItemLabel;
	/** `restore` in a deleted-items list: "Restore selected" instead of "Delete selected" */
	mode?: 'delete' | 'restore';
	/** The action is running (delete or restore) */
	isDeleting?: boolean;
	onClear: () => void;
	/** Runs the confirmed action, a delete or (in `restore` mode) a restore */
	onDelete: () => void | Promise<void>;
	/** Names of the selected items, listed in the confirmation (used for assets, the riskiest bulk delete). */
	itemNames?: string[];
	/** Extra warning in the delete confirmation, e.g. what goes with deleted assets */
	warning?: string;
	/** Text of the delete confirmation instead of "Deleted items can be restored…", e.g. for a permanent removal */
	description?: string;
	/** Told when the confirmation opens or closes, e.g. to load a preview of the selection */
	onConfirmOpenChange?: (open: boolean) => void;
	/** Extra content of the confirmation, e.g. per-item results of a preview */
	confirmBody?: ReactNode;
	/** Label of the confirm button, e.g. "Delete 8 of 10" when some items are left out */
	confirmLabel?: string;
	confirmDisabled?: boolean;
};

// Longer selections are summarised as "and N more"
const MAX_LISTED_NAMES = 10;

/**
 * Shown above a list while rows are selected: the count, a way to clear the
 * selection, and "Delete selected" (or, in a deleted-items list, "Restore selected")
 * behind a confirmation. Shared by every list that supports multi-select so they
 * all read the same.
 */
export function BulkActionBar({
	count,
	total,
	label,
	mode = 'delete',
	isDeleting = false,
	onClear,
	onDelete,
	itemNames,
	warning,
	description,
	onConfirmOpenChange,
	confirmBody,
	confirmLabel,
	confirmDisabled = false,
}: BulkActionBarProps) {
	const { t } = useTranslation();
	const noun = count === 1 ? label.singular : label.plural;
	const restoring = mode === 'restore';
	return (
		<div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
			<span>{t('common.bulk.selectedOf', { count, total, items: total === 1 ? label.singular : label.plural })}</span>
			<div className="flex items-center gap-2">
				<Button type="button" variant="ghost" size="sm" onClick={onClear} disabled={isDeleting}>
					<X /> {t('common.actions.clear')}
				</Button>
				<AlertDialog onOpenChange={onConfirmOpenChange}>
					<AlertDialogTrigger asChild>
						{restoring ? (
							<Button type="button" size="sm" disabled={isDeleting}>
								{isDeleting ? <Spinner className="size-4" /> : <RotateCcw />}
								{t('common.bulk.restoreSelected')}
							</Button>
						) : (
							<DeleteButton label={t('common.bulk.deleteSelected')} pending={isDeleting} />
						)}
					</AlertDialogTrigger>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{restoring
									? t('common.bulk.restoreTitle', { count, item: noun })
									: t('common.confirm.deleteTitle', { count, item: noun })}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{restoring
									? t('common.bulk.restoreDescription')
									: (description ?? t('common.confirm.deleteDescription'))}
							</AlertDialogDescription>
							{!restoring && warning && (
								<p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
									<TriangleAlert className="mt-0.5 size-4 shrink-0" />
									{warning}
								</p>
							)}
							{itemNames && itemNames.length > 0 && (
								<ul className="max-h-48 list-disc space-y-0.5 overflow-y-auto pl-5 text-sm">
									{itemNames.slice(0, MAX_LISTED_NAMES).map((itemName, index) => (
										<li key={`${index}-${itemName}`}>{itemName}</li>
									))}
									{itemNames.length > MAX_LISTED_NAMES && (
										<li className="list-none text-muted-foreground">
											{t('common.bulk.andMore', { count: itemNames.length - MAX_LISTED_NAMES })}
										</li>
									)}
								</ul>
							)}
							{confirmBody}
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>{t('common.actions.cancel')}</AlertDialogCancel>
							<AlertDialogAction
								className={restoring ? undefined : buttonVariants({ variant: 'destructive' })}
								disabled={confirmDisabled}
								onClick={() => void onDelete()}
							>
								{confirmLabel ?? (restoring ? t('common.actions.restore') : t('common.actions.delete'))}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</div>
	);
}
